<template>
  <v-card :disabled="refreshLoading" :loading="refreshLoading" rounded="xl" class="mb-4">
    <v-list color="grey">
      <v-list-item :dense="$vuetify.breakpoint.xsOnly">
        <v-list-item-icon v-if="!($vuetify.breakpoint.xsOnly || $vuetify.breakpoint.smOnly)">
          <v-icon size="36">
            {{ icons.mdiRecordCircleOutline }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title :style="!$vuetify.breakpoint.mdAndDown ? null : 'font-size:15px'">
            Satış Analiz Raporu
          </v-list-item-title>
          <v-tooltip bottom v-if="lastUpdateDate">
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-subtitle class="text-caption sales-report-last-update" v-bind="attrs" v-on="on">
                Son Güncelleme: {{ lastUpdateDate }}
              </v-list-item-subtitle>
            </template>
            <span>Son Güncelleme: {{ lastUpdateDate }}</span>
          </v-tooltip>
        </v-list-item-content>
        <v-spacer></v-spacer>
        <v-list-item-action>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn fab small depressed v-bind="attrs" v-on="on" @click="refreshData" :loading="refreshLoading">
                <v-icon>{{ icons.mdiRefresh }}</v-icon>
              </v-btn>
            </template>
            <span>Verileri Güncelle</span>
          </v-tooltip>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-card-actions class="grey">
      <v-row v-if="!$vuetify.breakpoint.mdAndDown" class="pt-3">
        <v-col cols="12" md="6">
          <v-select
            :disabled="loading"
            :items="birimElemanlar"
            v-model="birimDeger"
            label="Analiz Birimi"
            rounded
            filled
            dense
            background-color="nightDarkTon"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            :disabled="loading"
            :items="tarihElemanlar"
            v-model="tarihDeger"
            label="Analiz Tarihi"
            rounded
            filled
            dense
            background-color="nightDarkTon"
          ></v-select>
        </v-col>
      </v-row>
      <v-row v-else dense>
        <v-col cols="12">
          <v-select
            :disabled="loading"
            :items="tarihElemanlar"
            v-model="tarihDeger"
            rounded
            filled
            dense
            hide-details
            background-color="nightDarkTon"
          ></v-select>
        </v-col>
        <v-col cols="12">
          <v-select
            :disabled="loading"
            :items="birimElemanlar"
            v-model="birimDeger"
            dense
            rounded
            hide-details
            filled
            background-color="nightDarkTon"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-actions>
    <v-divider></v-divider>
    <v-card-text>
      <div class="satis_analiz--elemanlar">
        <div class="satis_analiz_eleman--kapsul" v-for="(eleman, index) in elemanlar" :key="index">
          <div class="satis_analiz_eleman--yuzey">
            <div class="satis_analiz_eleman--cerceve">
              <div class="satis_analiz_eleman--icerik">
                <div class="satis_analiz_eleman_kod satis_analiz_eleman--meta">
                  <v-tooltip right color="secondary">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        class="text-caption ml-3 sales-report-product-text"
                        v-bind="attrs"
                        v-on="on"
                        style="font-size: 11px !important"
                      >
                        {{ eleman.text || eleman.brand || eleman.id }}
                      </div>
                    </template>
                    <span class="text-body-2">{{ eleman.text }}</span>
                  </v-tooltip>
                </div>
                <div class="satis_analiz_eleman_gorsel satis_analiz_eleman--meta">
                  <v-tooltip left color="white">
                    <template v-slot:activator="{ on, attrs }">
                      <v-avatar color="white" v-bind="attrs" v-on="on">
                        <img v-if="eleman.src" :src="eleman.src" />
                        <div v-else>Ş</div>
                      </v-avatar>
                    </template>
                    <img v-if="eleman.src" :src="eleman.src" width="300" />
                    <div v-else class="pa-4">Görsel Yok</div>
                  </v-tooltip>
                </div>
              </div>
              <div class="satis_analiz_eleman_yuzde" v-show="!$vuetify.breakpoint.mdAndDown">
                <!-- Intentionally blank: keep bar clean -->
              </div>
              <div class="satis_analiz_eleman_deger satis_analiz_eleman--meta">
                <span
                  class="white--text font-weight-black text-subtitle-2 ml-3 dataCount"
                  :data-sss="eleman.value"
                  style="white-space: nowrap"
                  >{{ eleman.value }}
                </span>
                <span class="white--text font-weight-black text-subtitle-2 ml-1">
                  {{
                    birimDeger === 'price'
                      ? 'TL'
                      : eleman.qunit || quantityUnitLabel
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { onMounted, ref, nextTick, watch } from '@vue/composition-api'

// Plugin
import gsap from 'gsap'
import moment from 'moment'

// icon
import { mdiRecordCircleOutline, mdiRefresh } from '@mdi/js'
import { postData } from '@utils'

export default {
  name: 'DashboardSalesAnalysisReport',
  setup() {
    const birimDeger = ref('price')
    const birimElemanlar = ref([
      { text: 'Tutar', value: 'price' },
      { text: 'Miktar', value: 'quantity' },
    ])

    const tarihDeger = ref([])
    const tarihElemanlar = ref([])

    const ilkDeger = ref(0)
    const elemanlar = ref([])
    const quantityUnitLabel = ref('-')

    const loading = ref(true)
    const refreshLoading = ref(false)
    const lastUpdateDate = ref(null)

    const numberWithCommas = x => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    const animasyon = () => {
      gsap.set('.satis_analiz--elemanlar', { autoAlpha: 1 })
      gsap.set('.satis_analiz_eleman_deger', { autoAlpha: 0 })
      gsap.fromTo(
        '.satis_analiz_eleman--cerceve',
        { y: 300, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          stagger: 0.1,
          duration: 0.7,
          ease: 'back.out(1)',
          onComplete: () => {
            gsap.to('.satis_analiz_eleman_deger', { autoAlpha: 1 })
            const items = document.querySelectorAll('.dataCount')
            gsap.from(items, {
              textContent: 0,
              duration: 3,
              ease: 'power1.in',
              snap: { textContent: 1 },
              stagger: {
                each: 0.1,
                onUpdate: function () {
                  this.targets()[0].innerHTML = numberWithCommas(Math.ceil(this.targets()[0].textContent))
                },
              },
            })

            document.querySelectorAll('.satis_analiz_eleman_yuzde').forEach((section, index) => {
              let width
              if (index === 0) {
                width = 'calc(100% + 40px)'
                ilkDeger.value = elemanlar.value[0]?.value || 0
              } else if (ilkDeger.value) {
                width = `calc(${(elemanlar.value[index].value * 100) / ilkDeger.value}% + 0px)`
              } else {
                width = '0px'
              }

              gsap.to(section, {
                width,
                paddingLeft: 40,
                duration: 3,
                ease: 'power1.in',
                onComplete: function () {
                  loading.value = false
                },
              })
            })
          },
        },
      )
    }

    const getMonthDateRange = (year, month) => {
      const startDate = moment([year, month])
      const endDate = moment(startDate).endOf('month')

      tarihElemanlar.value.push({
        text: moment(startDate).format('MMMM YYYY'),
        value: [`"${moment(startDate).format('DD.MM.YYYY')}"`, `"${moment(endDate).format('DD.MM.YYYY')}"`],
      })
    }

    const initDateRanges = () => {
      const d = new Date()
      for (let i = 0; i <= 11; i++) {
        getMonthDateRange(d.getFullYear(), d.getMonth())
        d.setMonth(d.getMonth() - 1)
      }
      if (tarihElemanlar.value.length && (!tarihDeger.value || !tarihDeger.value.length)) {
        tarihDeger.value = tarihElemanlar.value[0].value
      }
    }

    initDateRanges()

    const applyReportResponse = response => {
      elemanlar.value = (Array.isArray(response.data) ? response.data : []).map(item => ({
        ...item,
        value: Number(item?.value ?? 0),
      }))
      quantityUnitLabel.value = response?.qunit ?? '-'
      const date = new Date(Date.now())
      response.lastUpdateDate = date
      localStorage.setItem('SALESREPORT', JSON.stringify(response))
      lastUpdateDate.value = moment(date).format('DD.MM.YYYY HH:mm')
    }

    const fetchReport = async ({ isLive = false } = {}) => {
      const payload = {
        method: 'dashboardSalesReport',
        daterange: `[${tarihDeger.value}]`,
        type: 'product',
        unit: birimDeger.value || 'price',
      }

      if (isLive) {
        payload.isLive = 1
      }

      const response = await postData(payload)

      if (response.error === 0) {
        applyReportResponse(response)
        return true
      }

      throw new Error(response.msg)
    }

    const reloadReport = async ({ isLive = false, useRefreshLoading = false } = {}) => {
      if (!tarihDeger.value || !tarihDeger.value.length) return false

      if (useRefreshLoading) {
        refreshLoading.value = true
      } else {
        loading.value = true
      }

      gsap.set('.satis_analiz--elemanlar', { autoAlpha: 0 })

      try {
        const ok = await fetchReport({ isLive })
        if (ok) {
          gsap.set('.satis_analiz_eleman_yuzde', { width: 0 })
          await nextTick()
          document.querySelectorAll('.dataCount').forEach(item => {
            item.innerHTML = item.dataset.sss
          })
          animasyon()
        }
        return ok
      } catch (e) {
        return false
      } finally {
        if (useRefreshLoading) {
          refreshLoading.value = false
        } else {
          loading.value = false
        }
      }
    }

    const refreshData = () => reloadReport({ isLive: true, useRefreshLoading: true })

    watch(
      [birimDeger, tarihDeger],
      async ([, dateRange]) => {
        if (!dateRange || !dateRange.length) return
        await reloadReport()
      },
    )

    onMounted(async () => {
      gsap.set('.satis_analiz--elemanlar', { autoAlpha: 0 })
      birimDeger.value = birimDeger.value || 'price'

      const SALESREPORT = JSON.parse(localStorage.getItem('SALESREPORT'))
      if (SALESREPORT && SALESREPORT.lastUpdateDate) {
        lastUpdateDate.value = moment(SALESREPORT.lastUpdateDate).format('DD.MM.YYYY HH:mm')
      }

      await reloadReport()
    })

    return {
      loading,
      elemanlar,
      birimDeger,
      birimElemanlar,
      tarihDeger,
      tarihElemanlar,
      quantityUnitLabel,
      icons: {
        mdiRecordCircleOutline,
        mdiRefresh,
      },
      refreshLoading,
      lastUpdateDate,
      refreshData,
    }
  },
}
</script>

<style lang="scss">
.satis_analiz--kapsul {
  width: 100%;
  height: 100%;
}
.satis_analiz_eleman--kapsul {
  width: 100%;
}
.satis_analiz_eleman--yuzey {
  display: flex;
  align-items: center;
  padding: 7px 0;
}
.satis_analiz--yuzey {
  height: 100%;
}
.satis_analiz--elemanlar {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.satis_analiz_eleman--cerceve {
  display: flex;
  align-items: center;
  width: 100%;
}
.satis_analiz_eleman--icerik {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 380px;
  width: 520px;
  max-width: 100%;

  background: #201c2d;
  border-radius: 100px;
  position: relative;
  z-index: 10;
}
.satis_analiz_eleman_deger {
  white-space: nowrap;
}
.satis_analiz_eleman_yuzde {
  position: relative;
  z-index: 1;
  background: #0083e8;
  height: 58px;
  width: 0px;
  margin-left: -30px;
  border-top-right-radius: 100px;
  border-bottom-right-radius: 100px;
  display: flex;
  align-items: center;
  padding-left: 0px;
  overflow: hidden;
  white-space: nowrap;
}
.satis_analiz_eleman--meta {
  padding: 5px;
}

.sales-report-product-text {
  max-width: 420px; /* roughly ~70 chars across 2 lines */
  min-width: 0;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow-wrap: anywhere;
}

.satis_analiz_eleman_kod {
  flex: 1 1 420px;
  min-width: 0; /* critical: allow text to shrink, don't overlap avatar */
}

@media (max-width: 960px) {
  .satis_analiz_eleman--icerik {
    flex-basis: 100%;
    width: 100%;
  }

  .sales-report-product-text {
    max-width: 280px;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}

.sales-report-last-update {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  max-width: calc(100% - 8px);
  line-height: 1.2;
}
</style>

